import { Injectable } from '@angular/core';
import { AuthorizationService, GlobalEventBusService, GroupContextService, hd } from '@nx-fe/services';
import { Observable } from 'rxjs';
import { RemoteControlService } from './remote-control.service';
import { RemoteControlDeviceState, DeviceStateRegistryService } from './device-state-registry.service';
import { RcDeviceService } from './rc-device-service';
import { XsI18nPipe } from '@nx-fe/pipes';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { RcExecuteScriptComponent } from '../remote-control-view/rc-execute-script/rc-execute-script.component';
import { PromptDialogComponent } from 'app/shared/components/prompt-dialog/prompt-dialog.component';
import { GlobalEvents } from 'app/core/global-event-constants';

export interface IDeviceAction {
  Id: string;
  Name: string;
  Content: Function;
}

export class RemoteControlDynamicDeviceState {
  sendScriptCompatibilityResource: Observable<hd.IMcDeviceActionCompatibilityReport>;
  softResetCompatibilityResource: Observable<hd.IMcDeviceActionCompatibilityReport>;
  deviceActionScriptPermission = false;
  hasAdminModePermission = false;
  hasKioskModePermission = false;
  deviceSoftResetPermission = false;
  useKioskAndAdminPermissions: boolean;
  deviceActionList: IDeviceAction[] = [];
  needsDeviceNameTruncated = false;
}

/**
 * Remote Control device state detection service.
 *
 * This service is extracted from rc-title-bar.component.ts.
 * This service manages the state of remote control title bar for all rc sessions mapped to a specific device state.
 */
@Injectable()
export class RcDeviceStateChangeDetectService {
  private SendButton_Id = '1';
  private KioskMode_ON_Id = '2';
  private KioskMode_OFF_Id = '3';
  private SoftReset_Id = '4';
  private DebugLoggingMode_ON_Id = '5';
  private DebugLoggingMode_OFF_Id = '6';
  private AdminMode_ON_Id = '7';
  private AdminMode_OFF_Id = '8';

  constructor(
    private deviceService: RcDeviceService,
    private saI18nPipe: XsI18nPipe,
    private authorizationService: AuthorizationService,
    private groupContextService: GroupContextService,
    private remoteControlService: RemoteControlService,
    private deviceStateRegistry: DeviceStateRegistryService,
    private globalEventBusService: GlobalEventBusService,
    private dialog: MatDialog
  ) {}

  public deviceStateChanged(
    deviceState: RemoteControlDeviceState,
    dynamicState: RemoteControlDynamicDeviceState,
    isStandaloneRC: boolean
  ) {
    dynamicState.deviceActionList = [];
    if (!deviceState || !deviceState.device || deviceState.hasError) {
      dynamicState.needsDeviceNameTruncated = false;
      return;
    }

    dynamicState.needsDeviceNameTruncated = null;
    deviceState.recording$.subscribe((isRecording: boolean) => {
      dynamicState.needsDeviceNameTruncated = null;
    });
    dynamicState.useKioskAndAdminPermissions = this._compareVersionNumbers(deviceState.device.mcVersion, '15.3.1') >= 0;
    if (dynamicState.useKioskAndAdminPermissions) {
      // Find kiosk mode permission
      if (this.isValidDeviceForKioskMode(deviceState)) {
        const kioskCompatibilityResource = this.deviceService.getDevicePermissionCompatibilityResource(
          deviceState.device.deviceId,
          'EnableKioskMode',
          deviceState.device.mcConnectionId
        );
        kioskCompatibilityResource.subscribe(permission => {
          dynamicState.hasKioskModePermission = false;
          if (permission.compatibleAccessibleDevices) {
            const indexOfDevice = permission.compatibleAccessibleDevices.indexOf(
              deviceState.device.mcDeviceId ? deviceState.device.mcDeviceId : deviceState.device.deviceId
            );
            dynamicState.hasKioskModePermission = indexOfDevice !== -1;
          }

          if (dynamicState.hasKioskModePermission) {
            dynamicState.deviceActionList.push(
              {
                Name: this.saI18nPipe.transform('rcTitle_btn_KioskMode_ON'),
                Id: this.KioskMode_ON_Id,
                Content: () => this.setKioskModeON(deviceState),
              },
              {
                Name: this.saI18nPipe.transform('rcTitle_btn_KioskMode_OFF'),
                Id: this.KioskMode_OFF_Id,
                Content: () => this.setKioskModeOFF(deviceState),
              }
            );
          }

          dynamicState.deviceActionList.sort((actionA: IDeviceAction, actionB: IDeviceAction) =>
            actionA.Id.localeCompare(actionB.Id)
          );
        });
      }

      if (this.deviceService.isAndroidDevice(deviceState)) {
        // Find admin mode permission
        const adminCompatibilityResource = this.deviceService.getDevicePermissionCompatibilityResource(
          deviceState.device.deviceId,
          'EnableAdminMode',
          deviceState.device.mcConnectionId
        );
        adminCompatibilityResource.subscribe((permission: hd.IMcDeviceActionCompatibilityReport) => {
          dynamicState.hasAdminModePermission = false;
          if (permission.compatibleAccessibleDevices) {
            const indexOfDevice = permission.compatibleAccessibleDevices.indexOf(
              deviceState.device.mcDeviceId ? deviceState.device.mcDeviceId : deviceState.device.deviceId
            );
            dynamicState.hasAdminModePermission = indexOfDevice !== -1;
          }

          if (dynamicState.hasAdminModePermission) {
            dynamicState.deviceActionList.push(
              {
                Name: this.saI18nPipe.transform('rcTitle_btn_AdministratorMode_OFF'),
                Id: this.AdminMode_ON_Id,
                Content: () => this.setAdminModeON(deviceState),
              },
              {
                Name: this.saI18nPipe.transform('rcTitle_btn_UserMode_ON'),
                Id: this.AdminMode_OFF_Id,
                Content: () => this.setAdminModeOFF(deviceState),
              }
            );
          }

          dynamicState.deviceActionList.sort((actionA: IDeviceAction, actionB: IDeviceAction) =>
            actionA.Id.localeCompare(actionB.Id)
          );
        });
      }

      dynamicState.sendScriptCompatibilityResource = this.deviceService.getDevicePermissionCompatibilityResource(
        deviceState.device.deviceId,
        'SendScript',
        deviceState.device.mcConnectionId
      );
      dynamicState.sendScriptCompatibilityResource.subscribe((permission: hd.IMcDeviceActionCompatibilityReport) => {
        dynamicState.deviceActionScriptPermission = null;
        if (permission.compatibleAccessibleDevices) {
          const indexOfDevice = permission.compatibleAccessibleDevices.indexOf(
            deviceState.device.mcDeviceId ? deviceState.device.mcDeviceId : deviceState.device.deviceId
          );
          dynamicState.deviceActionScriptPermission = indexOfDevice !== -1;
        }
        if (dynamicState.deviceActionScriptPermission) {
          this.addSendScriptToActionMenu(deviceState, dynamicState);
          if (
            this.isValidDeviceForDebugLoggingMode(deviceState) &&
            !isStandaloneRC &&
            this.authorizationService.isAdminOrTechnician(this.groupContextService.currentGroupId)
          ) {
            dynamicState.deviceActionList.push(
              {
                Name: this.saI18nPipe.transform('rcTitle_btn_DebugLoggingMode_ON'),
                Id: this.DebugLoggingMode_ON_Id,
                Content: () => this.setDebugLoggingModeON(deviceState),
              },
              {
                Name: this.saI18nPipe.transform('rcTitle_btn_DebugLoggingMode_OFF'),
                Id: this.DebugLoggingMode_OFF_Id,
                Content: () => this.setDebugLoggingModeOFF(deviceState),
              }
            );
          }
        }
      });
    } else {
      // Find send script permission
      dynamicState.sendScriptCompatibilityResource = this.deviceService.getDevicePermissionCompatibilityResource(
        deviceState.device.deviceId,
        'SendScript',
        deviceState.device.mcConnectionId
      );
      dynamicState.sendScriptCompatibilityResource.subscribe((permission: hd.IMcDeviceActionCompatibilityReport) => {
        dynamicState.deviceActionScriptPermission = null;
        if (permission.compatibleAccessibleDevices) {
          const indexOfDevice = permission.compatibleAccessibleDevices.indexOf(
            deviceState.device.mcDeviceId ? deviceState.device.mcDeviceId : deviceState.device.deviceId
          );
          dynamicState.deviceActionScriptPermission = indexOfDevice !== -1;
        }

        if (dynamicState.deviceActionScriptPermission) {
          this.addSendScriptToActionMenu(deviceState, dynamicState);

          if (this.isValidDeviceForKioskMode(deviceState)) {
            if (this.deviceService.isAndroidDevice(deviceState)) {
              dynamicState.deviceActionList.push(
                {
                  Name: this.saI18nPipe.transform('rcTitle_btn_UserMode_ON'),
                  Id: this.KioskMode_ON_Id,
                  Content: () => this.setKioskModeByScriptON(deviceState),
                },
                {
                  Name: this.saI18nPipe.transform('rcTitle_btn_AdministratorMode_OFF'),
                  Id: this.KioskMode_OFF_Id,
                  Content: () => this.setKioskModeByScriptOFF(deviceState),
                }
              );
            } else if (this.deviceService.isWindowsDevice(deviceState)) {
              dynamicState.deviceActionList.push(
                {
                  Name: this.saI18nPipe.transform('rcTitle_btn_KioskMode_ON'),
                  Id: this.KioskMode_ON_Id,
                  Content: () => this.setKioskModeByScriptON(deviceState),
                },
                {
                  Name: this.saI18nPipe.transform('rcTitle_btn_KioskMode_OFF'),
                  Id: this.KioskMode_OFF_Id,
                  Content: () => this.setKioskModeByScriptOFF(deviceState),
                }
              );
            }
          }

          if (
            this.isValidDeviceForDebugLoggingMode(deviceState) &&
            !isStandaloneRC &&
            this.authorizationService.isAdminOrTechnician(this.groupContextService.currentGroupId)
          ) {
            dynamicState.deviceActionList.push(
              {
                Name: this.saI18nPipe.transform('rcTitle_btn_DebugLoggingMode_ON'),
                Id: this.DebugLoggingMode_ON_Id,
                Content: () => this.setDebugLoggingModeON(deviceState),
              },
              {
                Name: this.saI18nPipe.transform('rcTitle_btn_DebugLoggingMode_OFF'),
                Id: this.DebugLoggingMode_OFF_Id,
                Content: () => this.setDebugLoggingModeOFF(deviceState),
              }
            );
          }

          dynamicState.deviceActionList.sort((actionA: IDeviceAction, actionB: IDeviceAction) =>
            actionA.Id.localeCompare(actionB.Id)
          );
        }
      });
    }

    // Find soft reset permission
    dynamicState.softResetCompatibilityResource = this.deviceService.getDevicePermissionCompatibilityResource(
      deviceState.device.deviceId,
      'SoftReset',
      deviceState.device.mcConnectionId
    );
    dynamicState.softResetCompatibilityResource.subscribe((permission: hd.IMcDeviceActionCompatibilityReport) => {
      dynamicState.deviceSoftResetPermission = null;
      if (permission.compatibleAccessibleDevices && deviceState) {
        const indexOfDevice = permission.compatibleAccessibleDevices.indexOf(
          deviceState.device.mcDeviceId ? deviceState.device.mcDeviceId : deviceState.device.deviceId
        );
        dynamicState.deviceSoftResetPermission = indexOfDevice !== -1;
      }
      if (dynamicState.deviceSoftResetPermission) {
        dynamicState.deviceActionList.push({
          Name: this.saI18nPipe.transform('rcTitle_btn_SoftReset'),
          Id: this.SoftReset_Id,
          Content: () => this.resetDevice(deviceState),
        });
      }
      dynamicState.deviceActionList.sort((actionA: IDeviceAction, actionB: IDeviceAction) =>
        actionA.Id.localeCompare(actionB.Id)
      );
    });
  }

  private addSendScriptToActionMenu = (
    deviceState: RemoteControlDeviceState,
    dynamicState: RemoteControlDynamicDeviceState
  ) => {
    dynamicState.deviceActionList.push({
      Name: this.saI18nPipe.transform('sendScript_SendButton'),
      Id: this.SendButton_Id,
      Content: () => this.openScriptDialog(deviceState),
    });
  };

  private _compareVersionNumbers = (v1: string, v2: string): number => {
    if (!v1 || !v2) {
      return -1;
    }

    const v1parts = v1.split('.');
    const v2parts = v2.split('.');
    const len = Math.max(v1parts.length, v2parts.length);

    // Normalize lengths with 0 components
    // This ensures 10.0.1 > 10
    if (v1parts.length < v2parts.length) {
      for (let i = 0; i < len; i++) {
        v1parts.push('0');
      }
    } else if (v2parts.length < v1parts.length) {
      for (let i = 0; i < len; i++) {
        v2parts.push('0');
      }
    }

    // loop while the components are equal
    for (let i = 0; i < len; i++) {
      // A bigger than B
      if (parseInt(v1parts[i], 10) > parseInt(v2parts[i], 10)) {
        return 1;
      }

      // B bigger than A
      if (parseInt(v1parts[i], 10) < parseInt(v2parts[i], 10)) {
        return -1;
      }
    }

    // Otherwise they are the same.
    return 0;
  };

  private openScriptDialog = (deviceState: RemoteControlDeviceState) => {
    // this.errorHandlerService.clearError();
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '790px';
    dialogConfig.panelClass = 'custom-dialog-container';
    dialogConfig.closeOnNavigation = true;
    dialogConfig.data = {
      deviceId: deviceState.device.deviceId,
      mcConnectionId: deviceState.device.mcConnectionId,
    };

    this.dialog.open(RcExecuteScriptComponent, dialogConfig);
  };

  public resetDevice = (deviceState: RemoteControlDeviceState) => {
    if (deviceState && deviceState.device && this.deviceStateRegistry.isRecording(deviceState.device)) {
      return;
    }

    this.clearErrorOnDeviceActions();

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '400px';
    dialogConfig.height = '232px';
    dialogConfig.panelClass = 'custom-dialog-scrollable-container';
    dialogConfig.closeOnNavigation = true;
    dialogConfig.data = {
      title: this.saI18nPipe.transform('rcTitle_btn_SoftReset').toUpperCase(),
      icon: 'hd-icon-softreset',
      confirmButton: this.saI18nPipe.transform('rcTitle_btn_SoftReset'),
      inputTitle: this.saI18nPipe.transform('rcTitle_dialog_SoftReset'),
      placeHolder: this.saI18nPipe.transform('rcTitle_btn_SoftReset'),
    };

    const modalInstance = this.dialog.open(PromptDialogComponent, dialogConfig);
    modalInstance.afterClosed().subscribe((isConfirmed = false) => {
      if (isConfirmed) {
        const actionInfo: hd.IDeviceActionInfo = {
          Action: 'SoftReset',
          Message: 'Soft reset this device',
          QueueForOfflineDelivery: false,
        };

        this.deviceService.sendDeviceAction(deviceState.device.deviceId, actionInfo, this.onDeviceActionError);
      }
    });
  };

  private setKioskModeByScriptON = (deviceState: RemoteControlDeviceState) => {
    this.clearErrorOnDeviceActions();
    this.deviceService.setKioskMode(deviceState.device.deviceId, true, this.getDeviceFamily(deviceState));
  };

  private setKioskModeByScriptOFF = (deviceState: RemoteControlDeviceState) => {
    this.clearErrorOnDeviceActions();
    this.deviceService.setKioskMode(deviceState.device.deviceId, false, this.getDeviceFamily(deviceState));
  };

  private setKioskModeON = (deviceState: RemoteControlDeviceState) => {
    this.clearErrorOnDeviceActions();
    this.deviceService.setKioskModeByAction(
      deviceState.device.deviceId,
      true,
      this.getDeviceFamily(deviceState),
      this.onDeviceActionError
    );
  };

  private setKioskModeOFF = (deviceState: RemoteControlDeviceState) => {
    this.clearErrorOnDeviceActions();
    this.deviceService.setKioskModeByAction(
      deviceState.device.deviceId,
      false,
      this.getDeviceFamily(deviceState),
      this.onDeviceActionError
    );
  };

  private setAdminModeON = (deviceState: RemoteControlDeviceState) => {
    this.clearErrorOnDeviceActions();
    this.deviceService.setAdminMode(
      deviceState.device.deviceId,
      true,
      this.getDeviceFamily(deviceState),
      this.onDeviceActionError
    );
  };

  private setAdminModeOFF = (deviceState: RemoteControlDeviceState) => {
    this.clearErrorOnDeviceActions();
    this.deviceService.setAdminMode(
      deviceState.device.deviceId,
      false,
      this.getDeviceFamily(deviceState),
      this.onDeviceActionError
    );
  };

  private setDebugLoggingModeON = (deviceState: RemoteControlDeviceState) => {
    this.clearErrorOnDeviceActions();
    this.deviceService.setDebugLoggingMode(deviceState.device.deviceId, true, this.getDeviceFamily(deviceState));
  };

  private setDebugLoggingModeOFF = (deviceState: RemoteControlDeviceState) => {
    this.clearErrorOnDeviceActions();
    this.deviceService.setDebugLoggingMode(deviceState.device.deviceId, false, this.getDeviceFamily(deviceState));
  };

  private clearErrorOnDeviceActions = () => {
    this.globalEventBusService.broadcast(GlobalEvents.deviceMenuErrorMessage, '');
  };

  private onDeviceActionError = (error: any) => {
    this.globalEventBusService.broadcast(GlobalEvents.deviceMenuErrorMessage, `Error: ${error}`);
  };

  private getDeviceFamily = (deviceState: RemoteControlDeviceState): string => {
    if (deviceState && deviceState.device) {
      return this.deviceService.getDeviceFamily(deviceState.device.kind, deviceState.device.family);
    }
  };

  private stopRemoteControl = (deviceState: RemoteControlDeviceState) => {
    if (deviceState && deviceState.device) {
      if (this.remoteControlService.stopRemoteControl(deviceState.device)) {
        this.globalEventBusService.broadcast(GlobalEvents.remoteControlCloseInitiated, null);
      }
    }
  };

  private isValidDeviceForKioskMode = (deviceState: RemoteControlDeviceState): boolean =>
    this.deviceService.isAndroidDevice(deviceState) || this.deviceService.isWindowsDevice(deviceState);

  private isValidDeviceForDebugLoggingMode = (deviceState: RemoteControlDeviceState): boolean =>
    this.deviceService.isAndroidDevice(deviceState);
}
